import { useState } from "react";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";

export const fmt = (v, d = 1) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(d);
  return String(v);
};
export const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
export const height = (inches) => {
  if (!inches) return "—";
  const n = Math.round(Number(inches));
  return `${Math.floor(n / 12)}'${n % 12}"`;
};
export const humanize = (k) =>
  String(k).replace(/_pct$/i, " %").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const shortDate = (s) => {
  if (!s) return "—";
  const str = String(s);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T12:00:00` : str);
  return Number.isNaN(d.getTime()) ? str : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
export const positionsOf = (p) =>
  Array.isArray(p?.positions) && p.positions.length ? p.positions.join(" / ") : p?.position ?? "—";
/** The 1st position is the one a player shows under on the roster. */
export const firstPosition = (p) => (Array.isArray(p?.positions) && p.positions.length ? p.positions[0] : p?.position) || "—";
export const secondPosition = (p) => (Array.isArray(p?.positions) ? p.positions[1] : null) || null;
export const PracticeTag = ({ testid }) => (
  <span data-testid={testid} className="rounded-full bg-amber-300/15 px-2 py-0.5 font-mono-gd text-[10px] uppercase tracking-wider text-amber-200">Practice player</span>
);
export const StarterTag = ({ testid }) => (
  <span data-testid={testid} className="rounded-full bg-[#00E5FF]/15 px-2 py-0.5 font-mono-gd text-[10px] uppercase tracking-wider text-[#7DF3FF]">Starter</span>
);
export const CaptainBadge = ({ testid, className = "" }) => (
  <span data-testid={testid} title="Team captain" aria-label="Team captain" className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#39FF14] font-display text-[11px] font-black leading-none text-[#0A0E1A] ${className}`}>C</span>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14] ${className}`}>{children}</p>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-2xl border border-[#1E293B] bg-[#0F172A]/80 p-5 sm:p-6 ${className}`} {...rest}>{children}</div>
);

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
      <h1 data-testid="page-title" className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl">{title}</h1>
    </div>
    {children && <div className="no-print flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const StatTile = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[#1E293B] bg-[#162032] px-4 py-3">
    <p className="font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className={`font-mono-gd text-2xl font-bold tracking-tighter sm:text-3xl ${accent ? "text-[#39FF14]" : "text-white"}`}>{value}</p>
  </div>
);

export const Loader = ({ label = "Loading" }) => (
  <div data-testid="loading-state" className="flex items-center gap-3 py-10 text-slate-400">
    <Loader2 className="h-5 w-5 animate-spin text-[#39FF14]" />
    <span className="font-mono-gd text-xs uppercase tracking-[0.2em]">{label}</span>
  </div>
);

export const Empty = ({ title = "Nothing here yet", text }) => (
  <div data-testid="empty-state" className="flex flex-col items-start gap-2 rounded-xl border border-dashed border-[#1E293B] p-6 text-slate-400">
    <Inbox className="h-5 w-5 text-slate-500" />
    <p className="font-display text-lg font-bold uppercase text-slate-300">{title}</p>
    {text && <p className="text-sm">{text}</p>}
  </div>
);

export const ErrorNote = ({ error }) => (
  <div data-testid="error-state" className="flex items-start gap-3 rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-4 text-sm text-red-200">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#FF3B30]" />
    <span>{error}</span>
  </div>
);

export const Avatar = ({ name = "", src, size = "h-12 w-12" }) => {
  const [broken, setBroken] = useState(false);
  const initials = name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  if (src && !broken)
    return <img src={src} alt={name} onError={() => setBroken(true)} className={`${size} shrink-0 rounded-full object-cover ring-1 ring-[#1E293B]`} />;
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-[#162032] font-display text-lg font-bold text-[#39FF14] ring-1 ring-[#39FF14]/30`}>
      {initials || "?"}
    </div>
  );
};

export const TAGS = ["Top Target", "Monitoring", "Backup"];
const TAG_STYLE = {
  "Top Target": "bg-[#39FF14]/15 text-[#39FF14] ring-[#39FF14]/40",
  Monitoring: "bg-[#00E5FF]/15 text-[#00E5FF] ring-[#00E5FF]/40",
  Backup: "bg-amber-400/15 text-amber-300 ring-amber-400/40",
};
export const TagBadge = ({ tag }) =>
  tag ? (
    <span className={`rounded-full px-2.5 py-0.5 font-mono-gd text-[11px] uppercase tracking-wider ring-1 ${TAG_STYLE[tag] ?? "bg-slate-700 text-slate-200 ring-slate-600"}`}>{tag}</span>
  ) : null;

export const Btn = ({ children, variant = "lime", className = "", ...rest }) => {
  const styles = {
    lime: "bg-[#39FF14] text-[#0A0E1A] hover:bg-[#30D911] hover:shadow-[0_0_30px_rgba(57,255,20,0.35)]",
    ghost: "border border-[#1E293B] bg-[#162032] text-white hover:border-[#39FF14]/50",
    danger: "border border-[#FF3B30]/40 text-red-200 hover:bg-[#FF3B30]/15",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,box-shadow,border-color,transform] duration-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export const PillTabs = ({ tabs, value, onChange }) => (
  <div className="no-print mb-6 flex gap-1 overflow-x-auto rounded-full border border-[#1E293B] bg-[#0F172A] p-1">
    {tabs.map((t) => (
      <button
        key={t}
        data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => onChange(t)}
        className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${value === t ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:text-white"}`}
      >
        {t}
      </button>
    ))}
  </div>
);
